import qs from 'qs';
import { PermissionCode, SurveyJobSearchField } from 'types';

export const PATH = {
  DEFAULT: '/',
  LOGIN: '/login',
  AUTH_CALLBACK: '/auth/callback',
  AUTH_VERIFY_SSO: '/auth/verify-sso',
  LOGOUT: '/logout',
  HEALTH: '/health',
  SETTINGS_SSO_CONFIGURATION: '/settings/ssoConfiguration',
  SECURITY_EVENT_API: '/settings/securityEventAPI',
  PERCENTILES: '/percentiles',
  CUSTOM_PERCENTILES: '/percentiles/custom',
  SETTINGS_HRIS: '/settings/hris',
  SETTINGS_HRIS_SFTP: '/settings/hris/sftp',
  SETTINGS_HRIS_WORKDAY_RAAS: '/settings/hris/workdayRaas',
  SETTINGS_JOB_MAPPINGS_MODE: '/settings/jobMappingMode',
  SETTINGS_JDXPERT: '/settings/jdxpert',
  SETTINGS_AI_FEATURES: '/settings/aiFeatures',
  DATA_RESET: '/settings/reset',
  TABLE_PRICEJOB: ({ compositeId }: { compositeId?: number } = {}) => `/market/pricejob${compositeId ? `?${qs.stringify({ compositeId })}` : ''}`,
  TABLE_PRICEJOB_MASS: '/market/pricejobMass',
  TABLE_PRICEJOB_MASS_POLICY_HISTORY: '/market/pricejobMass?openPolicyHistory=true',
  TABLE_PAYMARKET: '/configure/paymarket',
  TABLE_TAGS: '/configure/tags',
  TABLE_SURVEYS: '/configure/surveys',
  TABLE_PARTICIPATION: '/configure/participation',
  TABLE_POLICIES: '/policies',
  RANGES: '/ranges',
  RANGE_ELIGIBILITY: ({ planId }: { planId?: number }) => `/ranges/eligibility?${qs.stringify({ planId })}`,
  RANGE_GUIDELINES: ({ planId }: { planId?: number }) => `/ranges/guidelines?${qs.stringify({ planId })}`,
  RANGE_SETS: ({ planId }: { planId?: number }) => `/ranges/sets?${qs.stringify({ planId })}`,
  RANGE_GUIDELINE_VIEW: ({ id, planId }: { id: number, planId: number }) => `/ranges/guidelines/view?${qs.stringify({ id, planId })}`,
  RANGE_SPREAD: '/ranges/rangeSpread',
  RANGE_ASSIGNMENT: '/ranges/assignment',
  TABLE_USERS: ({ dataAccessGroupId }: { dataAccessGroupId?: number } = {}) => dataAccessGroupId ? `/configure/users?${qs.stringify({ dataAccessGroupId })}` : '/configure/users',
  TABLE_ROLES: '/configure/roles',
  DATA_ACCESS_GROUPS: '/configure/dataAccessGroup',
  ASSIGN_POLICIES: '/policies/assign',
  ROLE_PERMISSIONS: (roleId: number) => `/configure/roles/permissions?${qs.stringify({ roleId })}`,
  FILES_UPLOAD: '/configure/files',
  TABLE_PLANS: '/plans',
  TABLE_CURRENCY: '/currency',
  TABLE_JOB_MAPPING: '/mapping/job',
  JOB_MAPPING_RECOMMENDATIONS: ({
    planId,
    surveyJobProfileId,
    companyJobFamilyId,
    companyJobSubFamilyId,
    surveyJobFocusIds,
    surveyJobFamilyIds,
    surveyJobSubFamilyIds,
  }: JobMappingRecommendationsParams) => `/mapping/job/recommendations?${qs.stringify({
      planId,
      surveyJobProfileId,
      companyJobFamilyId,
      companyJobSubFamilyId,
      surveyJobFocusIds,
      surveyJobFamilyIds,
      surveyJobSubFamilyIds,
    }, { arrayFormat: 'brackets' })}`,
  TABLE_LEVEL_MAPPING: '/mapping/level',
  VISUAL_LEVEL_MAPPING: '/mapping/level/visual',
  MULTI_LEVEL_MAPPING: '/mapping/level/multi',
  LEVELS: '/levels',
  CATEGORIES: '/categories',
  TABLE_PAYMARKET_MAPPING: '/mapping/paymarket',
  TABLE_EMPLOYEES: '/employees',
  CONFIGURE_AGING_RULES: '/configure/agingRules',
  UPLOAD_EMPLOYEES: '/upload/employees',
  UPLOAD_SURVEYS: '/upload/surveys',
  UPLOAD_RANGES: '/upload/ranges',
  UPLOAD_JOBS: '/upload/jobs',
  COMPARE_CUTS: '/compare/cuts',
  REPORT: '/report',
  COMPANY_JOBS: '/jobs',
  COMPANY_JOBS_REWRITES: '/ranges/rewrites',
  COMPANY_JOBS_FAMILIES: '/jobs/families',
  COMPANY_JOBS_SUB_FAMILIES: '/jobs/subFamilies',
  EMPLOYEES_FILES: (id: number) => `/employees/files?${qs.stringify({ id })}`,
  REPORT_VIEW: (id: number, isGlobal?: boolean) => `/report/view?${qs.stringify({ id, isGlobal })}`,
  MODEL: '/model',
  MODEL_SALARY_RANGE: ({ id, isRecalculateStats }: { id: number, isRecalculateStats?: boolean }) => `/model/salaryRange?${qs.stringify({ id, isRecalculateStats })}`,
  MODEL_SALARY_RANGE_SETS: ({ id }: { id: number }) => `/model/salaryRange/rangeSet?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_JOBS: ({ id }: { id: number }) => `/model/salaryRange/rangeJobs?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_SETTINGS_ELIGIBILITY: ({ id }: { id: number }) => `/model/salaryRange/eligibility?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_GUIDELINES: ({ id }: { id: number }) => `/model/salaryRange/guidelines?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_SETTINGS: ({ id }: { id: number }) => `/model/salaryRange/settings?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_MINIMUMS: ({ id }: { id: number }) => `/model/salaryRange/minimums?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_SPREAD: ({ id }: { id: number }) => `/model/salaryRange/rangeSpread?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_ASSIGNMENT: ({ id }: { id: number }) => `/model/salaryRange/rangeAssignment?${qs.stringify({ id })}`,
  DOWNLOADS: '/downloads',
  PARTICIPATION: ({ id }: { id: string }) => `/participation?${qs.stringify({ id })}`,
  PARTICIPATION_FIELDS_MAPPING: ({ id }: { id: string }) => `/participation/${id}/fieldsMapping`,
  PARTICIPATION_JOB_MAPPING: ({ id }: { id: string }) => `/participation/${id}/jobMapping`,
  QUICKSIGHT_DASHBOARD: (dashboardId: string, reportId?: string) => `/analyze/dashboards/view?${qs.stringify({ dashboardId, reportId })}`,
  QUICKSIGHT: '/analyze/dashboards',
  QUICKSIGHT_AUTHORING: '/analyze/authoring',
  DATA_LOADERS: '/configure/dataLoaders',
  DATA_MANAGEMENT: '/dataManagement',
  AI_ASSISTANT: '/aiAssistant',
};

export type JobMappingRecommendationsParams = {
  planId: number;
  surveyJobProfileId: string;
  companyJobFamilyId?: number;
  companyJobSubFamilyId?: number;
  surveyJobFocusIds?: string[];
  surveyJobFamilyIds?: string[];
  surveyJobSubFamilyIds?: string[];
};

export const REQUEST_TYPE = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
};

export const CELL_FORMATS = {
  number: 'number',
  currency: 'currency',
  percentage: 'percentage',
  date: 'date',
} as const;

type CurrencyPayloadProps = {
  fromCurrencyCode?: string,
  toCurrencyCode?: string,
  fromRate?: number,
  toRate?: number,
};

export type CurrencyPayload = {
  currency?: string;
  rate: number
}

export const DEFAULT_CURRENCY = 'USD';

export function getCurrencyPayload({
  fromCurrencyCode,
  toCurrencyCode,
  fromRate = 1,
  toRate = 1,
}: CurrencyPayloadProps): CurrencyPayload {
  return {
    currency: toCurrencyCode || fromCurrencyCode,
    rate: (toCurrencyCode && fromCurrencyCode && toCurrencyCode !== fromCurrencyCode)
      ? toRate/fromRate
      : 1,
  };
}

export const SurveyJobSearchFilterOptions = [
  {
    label: 'Survey Job Title',
    value: SurveyJobSearchField.JOB_TITLE,
  },
  {
    label: 'Survey Job Code',
    value: SurveyJobSearchField.JOB_CODE,
  },
  {
    label: 'Survey Job Description',
    value: SurveyJobSearchField.JOB_DESCRIPTION,
  },
]

export const PermissionTitle: { [K in PermissionCode]: string } = {
  [PermissionCode.VIEW_USERS]: 'View Users',
  [PermissionCode.MANAGE_USERS]: 'Manage Users',
  [PermissionCode.MANAGE_ROLES]: 'Manage Roles',
  [PermissionCode.MANAGE_PLANS]: 'Manage Plans',
  [PermissionCode.MANAGE_EMPLOYEES]: 'Manage Employees',
  [PermissionCode.VIEW_COMPOSITE_EMPLOYEES]: 'View Composite Employees',
  [PermissionCode.VIEW_SURVEY_PARTICIPATIONS]: 'View Survey Participation',
  [PermissionCode.MANAGE_PAYMARKETS]: 'Manage Paymarkets',
  [PermissionCode.MANAGE_CURRENCIES]: 'Manage Currencies',
  [PermissionCode.MANAGE_AGING]: 'Manage Aging Rules',
  [PermissionCode.MANAGE_TAGS]: 'Manage Tags',
  [PermissionCode.MANAGE_SURVEYS]: 'Manage Surveys',
  [PermissionCode.LOAD_FILES]: 'Load Files',
  [PermissionCode.MANAGE_LEVEL_MAPPINGS]: 'Manage Level Mappings',
  [PermissionCode.MANAGE_DATA_CUT_PAYMARKET_MAPPINGS]: 'Manage DataCut Paymarket Mappings',
  [PermissionCode.MANAGE_COMPANY_JOBS]: 'Manage Company Jobs',
  [PermissionCode.MANAGE_API_SETTINGS]: 'Manage API Settings',
  [PermissionCode.MANAGE_PRICING_POLICIES]: 'Manage Pricing Policies',
  [PermissionCode.MANAGE_TARGET_PERCENTILES]: 'Manage Target Percentiles',
  [PermissionCode.MANAGE_CUSTOM_PERCENTILES]: 'Manage Custom Percentiles',
  [PermissionCode.MANAGE_DATA_MODELS]: 'Manage Range Data Models',
  [PermissionCode.VIEW_ANALYZE_DASHBOARDS]: 'Analyze Dashboards',
  [PermissionCode.MANAGE_REPORTS]: 'Manage Reports',
  [PermissionCode.MARKET_PRICING]: 'Market Pricing',
  [PermissionCode.MANAGE_JOB_MAPPING_MODE]: 'Manage Job Mappings Mode',
  [PermissionCode.MANAGE_DATA_ACCESS_GROUPS]: 'Manage Data Access Groups',
  [PermissionCode.MANAGE_DATA_LOADER]: 'Manage Data Loader',
  [PermissionCode.UPLOAD_DATA_FILES]: 'Upload Data Files',
  [PermissionCode.VIEW_DATA_HISTORY]: 'View Data History',
  [PermissionCode.CANCEL_DATA_LOADS]: 'Cancel Data Loads',
  [PermissionCode.EDIT_DATA_TRANSFORMATIONS]: 'Edit Data Transformations',
  [PermissionCode.DOWNLOAD_DATA_ERRORS]: 'Download Data Errors',
  [PermissionCode.BYPASS_SCHEMA_VALIDATION]: 'Bypass Schema Validation',
  [PermissionCode.REQUEST_DATA_SUPPORT]: 'Request Data Support',
  [PermissionCode.MANAGE_IDP]: 'Manage Idp',
  [PermissionCode.REPLACE_CUT_MAPPINGS]: 'Replace Cut Mappings',
  [PermissionCode.MANAGE_SECURITY_EVENT_API]: 'Manage Security Event API',
  [PermissionCode.REPLACE_UPDATE_SURVEY]: 'Replace/Update Survey',
  [PermissionCode.MANAGE_RANGE_ASSIGNMENTS]: 'Create/Manage Range Assignments',
  [PermissionCode.MANAGE_TENANCY_SWITCHER]: 'Manage Tenancy Switcher',
  [PermissionCode.MANAGE_AI_FEATURES]: 'Manage AI Features',
  [PermissionCode.MASS_APPLY_PRICING_POLICIES]: 'Policy Manager',
  [PermissionCode.MASS_ENHANCED_APPLY_PRICING_POLICIES]: 'Mass Enhanced Apply Pricing Policies',
  [PermissionCode.ASK_BETTERCOMP]: 'Ask Bettercomp',
}

export const VERIFY_SSO_MESSAGE_SOURCE = 'bc-test-sso-configuration';

export const anonymousRoutes = [
  PATH.AUTH_CALLBACK,
  PATH.AUTH_VERIFY_SSO,
  PATH.LOGOUT,
  PATH.HEALTH,
];

export const MAPPING_SURVEY_JOB_PROFILE_SETTINGS_KEY = 'activeMappingSurveyJobProfile';
export const MAPPING_VENDOR_LEVEL_PROFILE_SETTINGS_KEY = 'activeVendorLevelProfile';

export const GLOBAL_LEVEL_NOT_APPLICABLE = 'notApplicable'

export const SURVEY_JOB_DESCRIPTION_COLUMN_TITLE = 'surveyJobDescription';
